import { create } from 'zustand';

import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';

type Recording = {
  duration: number;
  endedAt: string;
  startedAt: string | null;
  resolution: string | null;
  frameRate: number | null;
  url: string | null;
};

export type WitnessRecordings = Record<DtoId, Recording | null>;
export type WitnessDecodedRecordings = Record<DtoId, Blob | null>;

type RecordingForUser = {
  name: string;
  role: string;
  witnessId: DtoId | null;
};

export type IwRecordingState = {
  isStarted: boolean;
  testator: Recording | null;
  witnesses: WitnessRecordings;
  testatorDecoded: Blob | null;
  witnessesDecoded: WitnessDecodedRecordings;
  recordingFor: RecordingForUser | null;
  savedPreview: Blob | null;
};

type IwRecordingStore = IwRecordingState & {
  extractStateForCollection: () => Omit<
    IwRecordingState,
    'testatorDecoded' | 'witnessesDecoded' | 'savedPreview'
  >;
  setTestator: (testator: Recording | null) => void;
  setWintesses: (witnesses: WitnessRecordings) => void;
  setIsStarted: (isStarted: boolean) => void;
  setRecordingFor: (recordingFor: RecordingForUser | null) => void;
  setTestatorDecoded: (testatorDecoded: Blob | null) => void;
  setWitnessesDecoded: (witnessesDecoded: WitnessDecodedRecordings) => void;
  setSavedPreview: (savedPreview: Blob | null) => void;

  clear: () => void;
};

const getInitialState = (): IwRecordingState => ({
  isStarted: false,
  testator: null,
  witnesses: {},
  testatorDecoded: null,
  witnessesDecoded: {},
  recordingFor: null,
  savedPreview: null,
});

export const useIwRecordingStore = create<IwRecordingStore>((set, get) => ({
  ...getInitialState(),

  setTestator: (testator) => set({ testator }),
  setWintesses: (witnesses) => set({ witnesses }),
  setIsStarted: (isStarted) => set({ isStarted }),
  setRecordingFor: (recordingFor) => set({ recordingFor }),
  setTestatorDecoded: (testatorDecoded) => set({ testatorDecoded }),
  setWitnessesDecoded: (witnessesDecoded) => set({ witnessesDecoded }),
  setSavedPreview: (savedPreview) => set({ savedPreview }),

  clear: () => set({ ...getInitialState() }),

  extractStateForCollection: () => ({
    testator: get().testator,
    witnesses: get().witnesses,
    isStarted: get().isStarted,
    recordingFor: get().recordingFor,
  }),
}));
